import * as React from 'react';
import { flatten, keyBy } from 'lodash';

import { IUserFriend, UbisoftAPIService } from 'modules/ubisoft-api';
import { USER_FRIEND_STATUS } from 'modules/ubisoft-api/ubisoft-api.constants';
import { FriendsReferralApiService, IFRInviteeData, IPlayersOwnershipResponse } from '@sitegen/common/classes/friends-referral-api';
import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';
import { IFRFriendListLoaderStoreProps } from './fr-friend-list-loader.container';
import {
    FR_INVITE_STATUS,
    FR_INVITE_STATUS_TO_LIST_ORDER,
    IFRFriend,
    SELECT_ALL_FRIENDS_ANALYTICS,
    INVITE_SELECTED_FRIENDS_ANALYTICS
} from '../fr-friend-list-common';
import { FRFriendListUI } from '../fr-friend-list-ui';

/**
 * Initialize list of Friend Referral friends by merging data from several APIs
 * Store and send invites for selected friends
 */  
export class FRFriendListLoader extends React.Component<IFRFriendListLoaderStoreProps, IFRFriendListLoaderState> {
    public readonly FR_REQUEST_LIMIT: number = 50;
 
    constructor(props: IFRFriendListLoaderStoreProps) {
        super(props);
        this.state = {
            ready: false,
            inviting: false,
            friends: [],
            isFriendListExpanded: false,
            listOfCheckedFriends: new Set(),
            maxAvailableFriendsToInvite: 0,
            isFeedbackOpen: false,
            error: null
        };
    }

    public async componentDidMount(): Promise<void> {
        await this.initialize();
    }

    public async initialize(): Promise<void> {
        try {
            this.setState({ friends: [], listOfCheckedFriends: new Set(), ready: false });

            const ubiFriends: IUserFriend[] = await UbisoftAPIService.getFriendList();
            let friends: IFRFriend[] = await this.mapFriendsStatuses(
                ubiFriends.filter(ubiFriend => ubiFriend.state === USER_FRIEND_STATUS.FRIEND)
            );
            friends = this.sortFriendsByInviteStatus(friends);

            const maxAvailableFriendsToInvite: number = friends.filter((f: IFRFriend): boolean => {
                return f.status === FR_INVITE_STATUS.AVAILABLE;
            }).length;

            this.setState({ friends, maxAvailableFriendsToInvite, ready: true });
        } catch (e) {
            this.setState({ error: e, ready: true });
        }
    }

    public handleCloseFeedback = async (): Promise<void> => {
        this.setState({ isFeedbackOpen: false, isFriendListExpanded: false, error: null });

        /* Re-init data after successful invite request */
        if (!this.state.error) {
            await this.initialize();
        }
    };

    public handleInviteClick = async (): Promise<void> => {
        if (!this.state.listOfCheckedFriends.size) {
            return;
        }

        this.setState({ inviting: true });
        try {
            await FriendsReferralApiService.inviteFriends(Array.from(this.state.listOfCheckedFriends));
            this.setState({ inviting: false, isFeedbackOpen: true });
            AnalyticsService.sendClickEvent(INVITE_SELECTED_FRIENDS_ANALYTICS);
        } catch (e) {
            this.setState({ inviting: false, isFeedbackOpen: true, error: e });
        }
    };

    public handleExpandListClick = (): void => {
        this.setState({ isFriendListExpanded: true });
    };

    public handleCheckFriend = (ubiId: string): void => {
        const list: Set<string> = new Set(this.state.listOfCheckedFriends);
        list.has(ubiId) ? list.delete(ubiId) : list.add(ubiId);
        this.setState({ listOfCheckedFriends: list });
    };

    public handleCheckAll = () => {
        if (this.state.listOfCheckedFriends.size >= this.state.maxAvailableFriendsToInvite) {
            this.setState({ listOfCheckedFriends: new Set() });
            return;
        }

        const listOfCheckedFriends: Set<string> = new Set();

        for (const friend of this.state.friends) {
            if (friend.status === FR_INVITE_STATUS.AVAILABLE) {
                listOfCheckedFriends.add(friend.ubiId);
            }
        }

        this.setState({ listOfCheckedFriends: listOfCheckedFriends });
        AnalyticsService.sendClickEvent(SELECT_ALL_FRIENDS_ANALYTICS);
    };

    private async collectBatchRequests<T>(ubiFriends: IUserFriend[], request: (ids: string[]) => Promise<T[]>): Promise<T[]> {
        const requestsAmount = Math.ceil(ubiFriends.length / this.FR_REQUEST_LIMIT);
        const promises: Promise<T[]>[] = [];

        for (let i = 0; i < requestsAmount; ++i) {
            const indexFrom = i * this.FR_REQUEST_LIMIT;
            const indexTo = indexFrom + this.FR_REQUEST_LIMIT;

            const ids = ubiFriends.slice(indexFrom, indexTo).map(({ pid }) => pid);

            promises.push(request(ids));
        }

        return flatten(await Promise.all(promises));
    }

    public async getOwnerFriends(ubiFriends: IUserFriend[]): Promise<IPlayersOwnershipResponse> {
        const friends = await this.collectBatchRequests(
            ubiFriends, 
            (ids: string[]) => FriendsReferralApiService.getPlayersOwnership(ids)
        );
        return friends.filter(player => player.platformOwnership.some(({ hasOwnership }) => hasOwnership));
    }

    public async getInvitedFriends(ubiFriends: IUserFriend[]): Promise<IFRInviteeData[]> {
        return this.collectBatchRequests(
            ubiFriends, 
            (ids: string[]) => FriendsReferralApiService.getPlayers(ids)
        );
    }

    /** Merge users lists and calculate all required data to send invites */
    public async mapFriendsStatuses(ubiFriends: IUserFriend[]): Promise<IFRFriend[]> {
        const result: IFRFriend[] = [];

        const owners = await this.getOwnerFriends(ubiFriends);
        const ownersById = keyBy(owners, owner => owner.ubiAccountId);

        const notOwners = ubiFriends.filter(friend => !ownersById[friend.pid]);
        const alreadyInvited = await this.getInvitedFriends(notOwners);

        const ubiFriendsById = keyBy(ubiFriends, friend => friend.pid);
        const alreadyInvitedById = keyBy(alreadyInvited, friend => friend.ubiAccountId);

        const ubiIdList: string[] = Object.keys(ubiFriendsById);

        for (const ubiId of ubiIdList) {
            /* Status rules are made according to https://mdc-tomcat-jira35.ubisoft.org/jira/browse/GEN-2119 */
            let status: FR_INVITE_STATUS = FR_INVITE_STATUS.AVAILABLE;
            
            if (ownersById[ubiId]) {
                status = FR_INVITE_STATUS.OWNS_THE_GAME;
            }

            if (alreadyInvitedById[ubiId]) {
                status = FR_INVITE_STATUS.INVITED;
            }

            result.push({ ubiId, status, name: ubiFriendsById[ubiId].nameOnPlatform });
        }

        return result;
    }

    /** Eligible friends should come first in the list */
    public sortFriendsByInviteStatus(friends: IFRFriend[]): IFRFriend[] {
        const comparator = (a: IFRFriend, b: IFRFriend): number => {
            if (a.status === b.status) {
                return a.name.localeCompare(b.name);
            }

            return FR_INVITE_STATUS_TO_LIST_ORDER[a.status] - FR_INVITE_STATUS_TO_LIST_ORDER[b.status];
        };

        return friends.sort(comparator);
    }

    public render(): JSX.Element {
        return (
            <FRFriendListUI
                env={this.props.env}
                ready={this.state.ready}
                inviting={this.state.inviting}
                friends={this.state.friends}
                listOfCheckedFriends={this.state.listOfCheckedFriends}
                maxAvailableFriendsToInvite={this.state.maxAvailableFriendsToInvite}
                isFriendListExpanded={this.state.isFriendListExpanded}
                isFeedbackOpen={this.state.isFeedbackOpen}
                error={this.state.error}

                onCheckFriend={this.handleCheckFriend}
                onCheckAll={this.handleCheckAll}
                onExpandListClick={this.handleExpandListClick}
                onInviteClick={this.handleInviteClick}
                onFeedbackClose={this.handleCloseFeedback}
            />
        );
    }
}

export interface IFRFriendListLoaderState {
    readonly ready: boolean;
    readonly inviting: boolean;
    readonly friends: IFRFriend[];
    readonly isFeedbackOpen: boolean;
    readonly isFriendListExpanded: boolean;
    /* Required for a faster calculation of "Select" and "Select All" states */
    readonly maxAvailableFriendsToInvite: number;
    readonly listOfCheckedFriends: Set<string>;
    readonly error: Error | null;
}
